import {ClassComponent, Vnode, VnodeDOM} from 'mithril';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import humanTime from 'flarum/common/helpers/humanTime';
import icon from 'flarum/common/helpers/icon';
import ValueHistory from '../utils/ValueHistory';

const translationPrefix = 'kilowhat-formulaire.forum.save-controls.';

interface SaveControlsAttrs {
    dirty?: boolean
    loading?: boolean
    savedAt?: Date | null
    inError?: boolean
    onsave?: () => void
    history?: ValueHistory | null
    floating?: boolean
}

export default class SaveControls implements ClassComponent<SaveControlsAttrs> {
    previouslyPromptedForUser: boolean = false
    inCooldownPeriod: boolean = false
    updateOverlayWidth!: () => void

    view(vnode: Vnode<SaveControlsAttrs, this>) {
        const {
            dirty,
            loading,
            savedAt,
            inError,
            onsave, // Callback
            history, // providing a ValueHistory object shows the undo/redo controls
            floating, // Whether to enable the floating behavior
        } = vnode.attrs;

        // Prompt for user means whether the controls should be made clearly visible to the user
        // if floating behavior is enabled, the controls will float when this value is true
        let promptForUser: boolean = !!dirty;

        if (history) {
            promptForUser = promptForUser || history.canUndo() || history.canRedo();
        }

        // We don't want to hide the controls immediately after a save
        // if the value just changed back to false, we apply a cooldown period during which it will continue to be active
        if (this.previouslyPromptedForUser && !promptForUser) {
            this.inCooldownPeriod = true;

            setTimeout(() => {
                this.inCooldownPeriod = false;
                m.redraw();
            }, 1000);
        }
        this.previouslyPromptedForUser = promptForUser;
        promptForUser = promptForUser || this.inCooldownPeriod;

        let message = null;

        if (inError) {
            message = m('span.in-error', [
                icon('fas fa-exclamation-triangle'),
                ' ',
                app.translator.trans(translationPrefix + 'validation-errors'),
            ]);
        } else if (dirty) {
            message = app.translator.trans(translationPrefix + 'unsaved-changes');
        } else if (savedAt) {
            message = m('span.success', [
                icon('fas fa-check'),
                ' ',
                app.translator.trans(translationPrefix + 'saved-at', {
                    ago: humanTime(savedAt),
                }),
            ]);
        }

        return m('.FormulaireSaveWrapper', m('.FormulaireSave', {
            className: floating && promptForUser ? 'floating' : '',
        }, [
            m('.FormulaireSave-stretch', message),
            history ? [
                Button.component({
                    onclick: () => {
                        history.undo();
                    },
                    disabled: !history.canUndo() || loading,
                    className: 'Button',
                    icon: 'fas fa-undo',
                }, app.translator.trans(translationPrefix + 'undo')),
                Button.component({
                    onclick: () => {
                        history.redo();
                    },
                    disabled: !history.canRedo() || loading,
                    className: 'Button',
                    icon: 'fas fa-redo',
                }, app.translator.trans(translationPrefix + 'redo')),
            ] : null,
            Button.component({
                onclick: onsave,
                disabled: !dirty || loading,
                className: 'Button Button--primary FormulairePrimaryButton',
                icon: 'fas fa-save',
                loading,
            }, app.translator.trans(translationPrefix + 'save')),
        ]));
    }

    oncreate(vnode: VnodeDOM<SaveControlsAttrs, this>) {
        this.updateOverlayWidth = () => {
            const overlay = vnode.dom.querySelector('.FormulaireSave') as HTMLElement;
            const rect = vnode.dom.getBoundingClientRect();

            if (overlay && rect.width > 0) {
                overlay.style.width = rect.width + 'px';
            }
        };

        // When used inside of a modal, the code triggers while the modal is still animating and the width is wrong
        // In that case we'll delay the code so it calculates the width once we are certain the modal is open
        // The user shouldn't notice anything because before any width is set it should be non-floating and 100% width
        // Only if the modal open while the form is in unsaved state could the unset width be noticed until this code runs
        // At the time of writing all modals use floating:false so there's absolutely no way to notice
        // we could even skip calculating the width entirely when floating is false but we might use it in the future
        if (vnode.dom.closest('.Modal')) {
            setTimeout(this.updateOverlayWidth, 300);
        } else {
            this.updateOverlayWidth();
        }

        window.addEventListener('resize', this.updateOverlayWidth);
    }

    onremove() {
        window.removeEventListener('resize', this.updateOverlayWidth);
    }
}
